import React from 'react';
import { Divider, Typography } from '@mui/material';
import { BlocksRenderer, type BlocksContent } from '@strapi/blocks-react-renderer';
import { Preloader } from '@vedmarket/ui-kit';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { useGetHelpArticleQuery } from '../../api/help-categories';
import PageLayout from '../../components/page-layout';
import PageTitle from '../../components/page-title';
import CustomRating from '../../components/rating';
import { COLORS } from '../../config/constants';

import './article.scss';

const getTextFromChildren = (children: any[] = []): string => (
    children.map((child) => {
        if (child.text) return child.text
        if (child.children) return getTextFromChildren(child.children)
        return ''
    }).join('')
);

const blocksComponents = {
    paragraph: ({ children }: { children?: React.ReactNode }) => (
        <p>{children}</p>
    ),
    heading: ({ children, level }: { children?: React.ReactNode; level: number }) => (
        <Typography variant={level === 5 ? 'h5' : 'h6'}>
            {children}
        </Typography>
    ),
    list: ({ children, format }: { children?: React.ReactNode; format: 'ordered' | 'unordered' }) => {
        const Component = format === 'ordered' ? 'ol' : 'ul'

        return (
            <Component>
                {children}
            </Component>
        )
    },
    'list-item': ({ children }: { children?: React.ReactNode }) => (
        <li>{children}</li>
    ),
    image: ({ image }: { image: { url: string; alternativeText?: string | null } }) => (
        <img src={image.url} alt={image.alternativeText || ''}/>
    ),
    link: ({ children, url }: { children?: React.ReactNode; url: string }) => (
        <a href={url} target="_blank" rel="noreferrer">
            {children}
        </a>
    ),
}

const modifiersComponents = {
    bold: ({ children }: { children: React.ReactNode }) => <b>{children}</b>,
    italic: ({ children }: { children: React.ReactNode }) => <i>{children}</i>,
    underline: ({ children }: { children: React.ReactNode }) => <u>{children}</u>,
    strikethrough: ({ children }: { children: React.ReactNode }) => <s>{children}</s>,
    code: ({ children }: { children: React.ReactNode }) => <code>{children}</code>,
}

const ArticlePage = () => {

    const { t } = useTranslation()
    const [searchParams] = useSearchParams()
    const documentId = searchParams.get('documentId') || ''
    const [activeSectionIndex, setActiveSectionIndex] = React.useState(0)
    const { data, isFetching } = useGetHelpArticleQuery(documentId, {
        skip: !documentId,
    })
    const article = data?.data

    const headings = React.useMemo(() => (
        article?.body
            .map((block, index) => ({ block, index }))
            .filter(({ block }) => block.type === 'heading')
            .map(({ block, index }) => ({
                id: `article-section-${index}`,
                title: getTextFromChildren((block as HelpArticleBodyBlock & { children: any[] }).children),
                index,
            })) || []
    ), [article])

    const scrollToSection = (id: string) => {
        document.getElementById(id)?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        })
    }

    React.useEffect(() => {
        const handleScroll = () => {
            const nextActiveIndex = headings.reduce((activeIndex, section, index) => {
                const element = document.getElementById(section.id)

                if (!element) {
                    return activeIndex
                }

                return element.getBoundingClientRect().top <= 220 ? index : activeIndex
            }, 0)

            setActiveSectionIndex(nextActiveIndex)
        }

        handleScroll()
        window.addEventListener('scroll', handleScroll)

        return () => window.removeEventListener('scroll', handleScroll)
    }, [headings])

    const renderBlock = (block: HelpArticleBodyBlock, index: number) => {
        const heading = headings.find((item) => item.index === index)

        return (
            <div key={`${block.type}-${index}`} id={heading?.id}>
                <BlocksRenderer
                    content={[block] as BlocksContent}
                    blocks={blocksComponents}
                    modifiers={modifiersComponents}
                />
            </div>
        )
    }

    if (isFetching) {
        return (
            <PageLayout hasTitle>
                <Preloader />
            </PageLayout>
        )
    }

    return (
        <PageLayout
            hasTitle
            rightPanelTitle={t('common__text__contents')}
            rightPanelItems={headings.map(({ title }) => title)}
            onRightPanelItemClick={(index) => scrollToSection(headings[index].id)}
            rightPanelActiveItemIndex={activeSectionIndex}
        >
            <PageTitle
                title={article?.title || ''}
                subcategory={article?.helpCategory?.name}
                subcategoryLink={article?.helpCategory?.slug ? `/${article.helpCategory.slug}` : undefined}
            />
            <Divider sx={{
                marginTop: 3,
                marginBottom: 3,
            }}/>
            {article && (
                <div className='article-container'>
                    <Typography variant='h5' sx={{
                        display: 'flex',
                        alignItems: 'center',
                        fontWeight: 700,
                        letterSpacing: 0,
                        lineHeight: '32px',
                        a: {
                            fontWeight: 700,
                        },
                    }}>
                        {article.subtitle}
                    </Typography>
                    <Typography variant='body2' sx={{
                        marginTop: 3,
                        color: COLORS.LIGHT_TEXT_DISABLED,
                    }}>
                        {new Date(article.publishedAt).toLocaleDateString('ru-RU', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                        })}
                    </Typography>
                    <Divider sx={{
                        marginTop: 3,
                        marginBottom: 1,
                    }}/>
                    {article.body.map(renderBlock)}
                </div>
            )}
            <CustomRating/>
        </PageLayout>
    )
};

export default ArticlePage;
